import {
  Center,
  Drawer,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  IconButton,
  Spinner,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { PageHeading } from '@duke-hq/ui/components/PageHeading';
import { Trans, Plural } from '@lingui/react/macro';
import TableViewLayout from '@duke-hq/ui/components/TableViewLayout/TableViewLayout';
import { Pagination } from '@duke-hq/ui/components/Pagination/Pagination';
import { emptyPaginationResponse, parseSortOrder, SortOrder } from '@duke-hq/lib/pagination';
import { ArrowRightFromLine } from 'lucide-react';
import { Fragment, useMemo, useCallback } from 'react';
import { SearchInput } from '@duke-hq/ui/components/SearchInput';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import { usePagination } from '@duke-hq/ui/components/hooks/usePagination';
import { CenteredSpinner } from '@duke-hq/app-provider-web/components/Loading';
import { useSubSidebar } from '@duke-hq/ui/components/SubSidebar/useSubSidebar';
import { constructURL } from '@duke-hq/lib';
import pickBy from 'lodash-es/pickBy.js';
import { TeamMemberFormModal } from '@duke-hq/app-provider-web/tenant/Team/TeamMemberFormModal';
import { TeamMemberDetails } from '@duke-hq/app-provider-web/tenant/Team/TeamMemberDetails';
import { TeamMembersTable } from '@duke-hq/app-provider-web/tenant/Team/TeamMembersTable';
import { TeamBulkEnrolmentsFormModal } from '@duke-hq/app-provider-web/tenant/Team/TeamBulkEnrolments/TeamBulkEnrolmentsFormModal';

interface SortFilters {
  orderBy: string | undefined;
  sort: SortOrder | undefined;
  page: string | undefined;
}

export function TeamMembers() {
  const { isExpanded, toggleExpandSubSidebar } = useSubSidebar();
  const { consultantId } = useParams<{ consultantId: string }>();
  const { currentPage, pageSize, setPage } = usePagination();
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams({
    orderBy: ['name'],
    sort: [SortOrder.ASC, SortOrder.DESC],
    page: '1',
  });

  const querySearch = searchParams.get('search') ?? '';

  const getURLFilters = useMemo(() => {
    const urlFilters = Object.fromEntries(searchParams.entries());
    const { page, orderBy, sort, ...rest } = urlFilters;
    return {
      ...rest,
      orderBy: orderBy ?? 'created_at',
      sort: parseSortOrder(sort) ?? SortOrder.DESC,
    };
  }, [searchParams]);

  const handleFilterChange = useCallback(
    (newFilters: Partial<SortFilters>) => {
      const updatedFilters = pickBy(
        { ...Object.fromEntries(searchParams.entries()), ...newFilters },
        (value) => value != null && value != '',
      );

      if (updatedFilters.page != null && updatedFilters.page === '1') {
        delete updatedFilters.page;
      }

      const newSearchParams = new URLSearchParams(updatedFilters as Record<string, string>);
      setSearchParams(newSearchParams);
    },
    [searchParams, setSearchParams],
  );

  const { data: members, isFetching } = trpcTenant.users.listMembersWithPagination.useQuery(
    {
      search: querySearch.trim(),
      filters: getURLFilters,
      pagination: {
        page: Number(currentPage),
        limit: pageSize,
      },
    },
    {
      initialData: emptyPaginationResponse(),
      placeholderData: (previousData) => previousData,
    },
  );

  const { data: hasInternalEnrolment, isLoading: isLoadingEnrolment } =
    trpcTenant.enrolments.hasInternalCourseEnrolments.useQuery();

  const { isOpen } = useDisclosure({
    isOpen: !!consultantId,
  });

  const onCloseHandler = useCallback(() => {
    const baseUrl = `/team/members`;
    const url = constructURL(baseUrl, getURLFilters);
    navigate(url);
  }, [navigate, getURLFilters]);

  return (
    <>
      {consultantId != null ? (
        <Drawer isOpen={isOpen} onClose={onCloseHandler} size="xl">
          <DrawerOverlay />
          <DrawerContent>
            <DrawerCloseButton onClick={onCloseHandler} />
            {isLoadingEnrolment ? (
              <CenteredSpinner />
            ) : (
              <TeamMemberDetails hasInternalEnrolment={hasInternalEnrolment ?? false} />
            )}
          </DrawerContent>
        </Drawer>
      ) : null}

      <TableViewLayout
        pageHeader={[
          <Fragment key="item">
            {!isExpanded && (
              <IconButton
                aria-label="expand secondary sidebar"
                icon={<ArrowRightFromLine />}
                variant="tertiary"
                onClick={toggleExpandSubSidebar}
                size="md"
              />
            )}
            <PageHeading>
              <Trans>Members</Trans>
            </PageHeading>
          </Fragment>,
        ]}
        pageActions={[
          hasInternalEnrolment === true && (
            <TeamBulkEnrolmentsFormModal key={'teamBulkEnrolmentsFormModal'} />
          ),
          <TeamMemberFormModal key={'teamMemberFormModal'} />,
        ]}
        filters={<SearchInput maxW="search-input" />}
        tableActionsLeft={
          <Text fontSize="md" fontWeight="semibold" color="fg.subtle">
            <Plural
              value={members?.pagination.totalCount || 0}
              zero="0 members"
              one="1 member"
              other="# members"
            />
          </Text>
        }
        table={
          isFetching ? (
            <Center p={8}>
              <Spinner size="xl" transitionDelay={'2900ms'} />
            </Center>
          ) : Array.isArray(members.items) && members.items.length > 0 ? (
            <TeamMembersTable
              members={members.items}
              urlParams={getURLFilters}
              onFilters={handleFilterChange}
            />
          ) : (
            <Center p={8}>
              <Text textAlign="center">
                <Trans>No members found</Trans>
              </Text>
            </Center>
          )
        }
        pagination={
          Array.isArray(members.items) && members.items.length > 0 ? (
            <Pagination pagination={members.pagination} onPageChange={setPage} />
          ) : null
        }
      />
    </>
  );
}
